import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * تنبيه أمني مؤقت يظهر على شاشة تسجيل الدخول خلال حملة إعادة التعيين.
 *
 * - ينتهي تلقائياً: يُقرأ التاريخ من REACT_APP_SECURITY_NOTICE_UNTIL (ISO). إن لم
 *   يُضبط أو مضى التاريخ فلا يُعرض شيء إطلاقاً (لا حاجة لنشر جديد لإزالته).
 * - قابل للإغلاق والإخفاء: يُحفظ الإغلاق في localStorage بمفتاح مرتبط بتاريخ الحملة.
 * - سطر رقيق دائم يعيد فتح النافذة لمن أغلقها (حتى لا تُفقد بيانات الدعم بإغلاق واحد).
 * - للقراءة فقط: لا أزرار إعادة تعيين ولا حقول إدخال، لتقليل خطر التصيّد.
 */
const SecurityNoticeModal = () => {
  const { t } = useTranslation();

  const until = process.env.REACT_APP_SECURITY_NOTICE_UNTIL;
  const untilTs = until ? Date.parse(until) : NaN;
  const active = !Number.isNaN(untilTs) && Date.now() <= untilTs;

  const dismissKey = useMemo(() => `securityNotice:${until || "none"}:dismissed`, [until]);

  const [show, setShow] = useState(() => {
    if (!active) return false;
    try {
      return !localStorage.getItem(dismissKey);
    } catch (e) {
      return true;
    }
  });

  if (!active) return null;

  // النطاق الرسمي: من البيئة إن وُجد، وإلا أصل الموقع الحالي (النطاق الذي يتصفّحه المستخدم فعلاً)
  const officialDomain =
    process.env.REACT_APP_SECURITY_NOTICE_DOMAIN ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || "";
  const supportPhone = process.env.REACT_APP_SUPPORT_PHONE || "";

  const close = () => {
    setShow(false);
    try {
      localStorage.setItem(dismissKey, "1");
    } catch (e) {
      /* تجاهل */
    }
  };

  return (
    <>
      <Modal show={show} onHide={close} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{t("security_notice_title", "Important security notice")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t(
              "security_notice_body",
              "As a precautionary security measure, all account and wallet passwords have been reset."
            )}
          </p>
          <p>
            {t(
              "security_notice_email",
              "A password reset link has been sent to your registered email address and is valid for 7 days. You can set both your account password and your wallet password from that page."
            )}
          </p>

          <hr />

          <p className="mb-1">
            <strong>{t("security_notice_no_email_title", "Didn't receive the email?")}</strong>
          </p>
          <ol className="mb-2">
            <li>{t("security_notice_check_spam", "Check your spam / junk folder.")}</li>
            <li>{t("security_notice_contact_support", "Contact technical support to verify your identity.")}</li>
          </ol>
          {(supportEmail || supportPhone) && (
            <p className="mb-2">
              {supportEmail && (
                <span>
                  {t("email", "Email")}: <strong>{supportEmail}</strong>
                </span>
              )}
              {supportEmail && supportPhone ? " — " : ""}
              {supportPhone && (
                <span>
                  {t("phone", "Phone")}: <strong>{supportPhone}</strong>
                </span>
              )}
            </p>
          )}

          <div className="alert alert-warning mt-2 mb-0">
            <p className="mb-1">
              <strong>{t("security_notice_phishing_title", "Beware of phishing")}</strong>
            </p>
            <p className="mb-1">
              {t(
                "security_notice_official_domain",
                "The only official place to set your password is:"
              )}{" "}
              <strong>{officialDomain}</strong>
            </p>
            <p className="mb-0">
              {t(
                "security_notice_never_ask",
                "Support will never ask you for your password through any channel."
              )}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            {t("close", "Close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {!show && (
        <div className="text-center mt-2">
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ fontSize: "0.85rem" }}
            onClick={() => setShow(true)}
          >
            {t("security_notice_reopen", "Security notice: didn't receive the reset email?")}
          </button>
        </div>
      )}
    </>
  );
};

export default SecurityNoticeModal;
